/** New explicit marker only. Reads never dispatch; selecting never prepares a script. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../i18n/language.js';
import { Button, Field, Modal, SidePanel, Tide } from '../../merchant/ui/primitives.js';
import { workspaceCopy } from '../../merchant/copy.js';
import { useWorkspace } from '../../merchant/shell-context.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import {
  fetchSavedBrief,
  fetchSavedScript,
  selectSavedIdea,
  requestSavedScript,
  type SavedBriefJourney,
  type SavedScriptView,
} from './savedBriefApi.js';
import { fetchAdjustmentCapability, requestIdeaAdjustment } from './firstIdeaApi.js';
import { FirstIdeaOption } from './FirstIdeaOption.js';
import { IdeaJourneyMap } from './FirstIdeaEntry.js';
import { ResearchReferenceList } from './ResearchReferenceList.js';
import { SavedScriptReview } from './SavedScriptReview.js';
import { firstIdeaCopy } from './firstIdeaCopy.js';
import './first-idea.css';
/** Same operation identity survives a lost response/reload. No automatic mutation on mount. */
function operationKey(identity: string) {
  const name = `mouja:idea-operation:${identity}`;
  try {
    const saved = sessionStorage.getItem(name);
    if (saved) return saved;
    const key = newIdempotencyKey();
    sessionStorage.setItem(name, key);
    return key;
  } catch {
    return newIdempotencyKey();
  }
}
function savedAdjustment(identity: string) {
  try {
    return sessionStorage.getItem(`mouja:idea-adjustment:${identity}`) ?? '';
  } catch {
    return '';
  }
}
export function FirstIdeaJourneyPage({ initial }: { initial: SavedBriefJourney }) {
  const { refresh: refreshWorkspace } = useWorkspace();
  const { language } = useLanguage(),
    c = firstIdeaCopy[language],
    ws = workspaceCopy(language);
  const [params, setParams] = useSearchParams(),
    version = params.get('version') ?? undefined,
    revision = params.get('ideaRevision') ?? undefined;
  const [data, setData] = useState(initial),
    [script, setScript] = useState<SavedScriptView | null>(null);
  const [candidate, setCandidate] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [failed, setFailed] = useState(false),
    [actionFailed, setActionFailed] = useState(false);
  const [evidence, setEvidence] = useState<string | null>(null),
    [adjust, setAdjust] = useState<string | null>(null),
    [adjustment, setAdjustment] = useState('');
  const [adjustmentAllowed, setAdjustmentAllowed] = useState(false);
  const reading = useRef(false),
    alive = useRef(true),
    actionKey = useRef<{ identity: string; key: string } | null>(null);
  const refresh = useCallback(async () => {
    if (reading.current) return;
    reading.current = true;
    try {
      const next = await fetchSavedBrief(
        initial.productionLineId,
        version,
        undefined,
        revision,
      );
      if (next.kind !== 'SAVED_BRIEF' || !next.firstIdea)
        throw new Error('Saved journey unavailable');
      const output = next.script ? await fetchSavedScript(next.script.publicId) : null;
      if (alive.current) {
        setData(next);
        setScript(output);
        setFailed(false);
      }
    } catch {
      if (alive.current) setFailed(true);
    } finally {
      reading.current = false;
    }
  }, [initial.productionLineId, version, revision]);
  useEffect(() => {
    alive.current = true;
    void refresh();
    const timer = window.setInterval(() => void refresh(), 3000);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
    };
  }, [refresh]);
  const view = data.firstIdea;
  useEffect(() => {
    // Keep list/sidebar state current after this page observes worker completion.
    // This is a read, not another research or generation command.
    void refreshWorkspace();
  }, [refreshWorkspace, view?.phase, data.brief.currentStage, script?.state]);
  useEffect(() => {
    if (!adjust || !view?.revision) return;
    try {
      sessionStorage.setItem(
        `mouja:idea-adjustment:${view.revision.revisionId}:${adjust}`,
        adjustment,
      );
    } catch {
      /* In-memory draft remains usable. */
    }
  }, [adjust, adjustment, view?.revision?.revisionId]);
  useEffect(() => {
    if (!view) return;
    let active = true;
    void fetchAdjustmentCapability(view.journeyId)
      .then((r) => {
        if (active) setAdjustmentAllowed(r.available);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [view?.journeyId]);
  if (!view) return null;
  const discovery = view.intent.mode === 'DISCOVER_FOR_ME',
    ideas = view.ideaSet?.ideas ?? [];
  // One developed idea needs confirmation, not an extra choice. This is UI-only:
  // the exact revision is still persisted only when the merchant clicks Use.
  const activeCandidate =
    view.intent.mode === 'DEVELOP_MY_IDEA' && ideas.length === 1
      ? (ideas[0]?.id ?? null)
      : candidate;
  const chosen = ideas.find(
    (idea) =>
      view.directions.find((d) => d.ideaId === idea.id)?.artifactId ===
      view.selected?.directionArtifactId,
  );
  const retained =
    view.ideaSet?.limitations.some(
      (r) => r.code === 'SEED_RETAINED_NOT_FULLY_DEVELOPED',
    ) ?? false;
  const historical = revision !== undefined && !view.isCurrent;
  const direction = view.directions.find((d) => d.ideaId === evidence);
  const latestJob = view.adjustments.at(-1);
  const canWrite = !failed && !busy && view.isCurrent;
  const goCurrent = () => {
    const next = new URLSearchParams(params);
    next.delete('ideaRevision');
    setParams(next);
  };
  async function useIdea() {
    if (!activeCandidate || !canWrite || !view?.actions.canSelect || !view.revision)
      return;
    const direction = view.directions.find((d) => d.ideaId === activeCandidate);
    if (!direction) return;
    setBusy(true);
    setActionFailed(false);
    try {
      await selectSavedIdea(
        {
          productionLineId: data.productionLineId,
          briefVersionId: data.brief.versionId,
          directionArtifactId: direction.artifactId,
          handoffArtifactId: view.revision.handoff?.id,
          expectedIdeaRevisionId: view.revision.revisionId,
          expectedIdeasHash: view.revision.ideas.contentHash,
        },
        operationKey(`select:${view.revision.revisionId}:${activeCandidate}`),
      );
      await refresh();
    } catch {
      setActionFailed(true);
    } finally {
      setBusy(false);
    }
  }
  async function prepare() {
    if (!canWrite || !view?.selected || !view.actions.canPrepareScript) return;
    setBusy(true);
    setActionFailed(false);
    try {
      await requestSavedScript(
        view.selected.selectionId,
        operationKey(`script:${view.selected.selectionId}`),
      );
      await refresh();
    } catch {
      setActionFailed(true);
    } finally {
      setBusy(false);
    }
  }
  async function update() {
    if (
      !adjust ||
      !adjustment.trim() ||
      !canWrite ||
      !view?.revision ||
      !view.actions.canAdjust
    )
      return;
    const body = {
      expectedBriefVersionId: data.brief.versionId,
      expectedIdeaRevisionId: view.revision.revisionId,
      ideaId: adjust,
      adjustmentText: adjustment,
    };
    const identity = JSON.stringify(body);
    if (actionKey.current?.identity !== identity)
      actionKey.current = { identity, key: operationKey(`adjust:${identity}`) };
    setBusy(true);
    setActionFailed(false);
    try {
      await requestIdeaAdjustment(view.journeyId, body, actionKey.current.key);
      setAdjust(null);
      await refresh();
    } catch {
      setActionFailed(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="mj-ws-page mj-first mj-ws-saved-journey" data-idea-journey="v1">
      <Link to="/production-lines">{c.lines}</Link>
      <IdeaJourneyMap
        step={view.selected ? 2 : 1}
        independent={view.intent.contentContext !== undefined}
      />
      <div className="mj-first-context">
        <span dir="auto">
          {view.intent.contentContext?.topic ??
            data.sourceProfile.payload.identity.value}
        </span>
        {view.intent.contentContext?.audience && (
          <span dir="auto">{view.intent.contentContext.audience}</span>
        )}
        <span>
          {c.language}: {view.intent.outputLocale === 'ar' ? 'العربية' : 'English'}
        </span>
      </div>
      {(failed || actionFailed) && (
        <div role="alert">
          <p>{failed ? c.reconnect : c.changeFailed}</p>
          <Button onClick={() => void refresh()}>{c.reload}</Button>
        </div>
      )}
      {historical && (
        <p className="mj-first-notice">
          {c.historical} <Button onClick={goCurrent}>{c.latest}</Button>
        </p>
      )}
      {view.phase === 'RESEARCHING' && (
        <section className="mj-first-progress" aria-live="polite">
          <Tide />
          <h1>
            {discovery
              ? view.intent.contentContext
                ? c.topicFinding
                : c.finding
              : c.developing}
          </h1>
          <p className="mj-first-progress-line">
            {data.brief.currentStage === 'STRATEGIC_OPPORTUNITIES'
              ? c.shaping
              : c.searching}
          </p>
          <p>{c.safe}</p>
        </section>
      )}
      {(view.phase === 'RECOVERY' || view.phase === 'NEEDS_INPUT') && (
        <section className="mj-first-progress">
          <h1>{view.phase === 'RECOVERY' ? c.failed : c.unavailable}</h1>
          {view.intent.originalSeed && (
            <blockquote dir="auto" className="mj-first-original">
              {view.intent.originalSeed}
            </blockquote>
          )}
          <Button onClick={() => void refresh()}>{c.reload}</Button>
          <p>
            <Link to="/production-lines/new">{c.newAttempt}</Link>
          </p>
        </section>
      )}
      {ideas.length > 0 && !view.selected && (
        <>
          <h1>
            {discovery
              ? ideas.length === 3
                ? c.three
                : c.partial
              : retained
                ? c.original
                : c.one}
          </h1>
          {!discovery && (
            <details>
              <summary>{c.original}</summary>
              <p className="mj-first-original" dir="auto">
                {view.intent.originalSeed}
              </p>
            </details>
          )}
          {retained && <p className="mj-first-notice">{c.retained}</p>}
          {view.phase === 'ADJUSTING' && (
            <p role="status" className="mj-first-notice">
              {c.adjusting}
            </p>
          )}
          {(latestJob?.state === 'FAILED' || latestJob?.state === 'CONFLICT') && (
            <div className="mj-first-notice">
              <p>{c.adjustFailed}</p>
              {view.actions.canAdjust && adjustmentAllowed && (
                <Button
                  onClick={() => {
                    setAdjust(latestJob.ideaId);
                    setAdjustment(latestJob.adjustmentText);
                    actionKey.current = null;
                  }}
                >
                  {c.adjust}
                </Button>
              )}
            </div>
          )}
          <div
            className={`mj-first-options${discovery ? ' is-discovery' : ''}`}
            role="radiogroup"
            aria-label={discovery ? c.partial : c.one}
          >
            {ideas.map((idea, index) => (
              <FirstIdeaOption
                key={idea.id}
                idea={idea}
                presentation={view.presentation}
                language={language}
                index={index}
                discovery={discovery}
                selected={activeCandidate === idea.id}
                disabled={!canWrite || !view.actions.canSelect}
                canAdjust={canWrite && view.actions.canAdjust && adjustmentAllowed}
                onSelect={() => {
                  setCandidate(idea.id);
                }}
                onEvidence={() => {
                  setEvidence(idea.id);
                }}
                onAdjust={() => {
                  setAdjust(idea.id);
                  setAdjustment(
                    savedAdjustment(`${view.revision?.revisionId ?? ''}:${idea.id}`),
                  );
                  actionKey.current = null;
                }}
              />
            ))}
          </div>
          {!historical && (
            <div className="mj-first-action">
              <Button
                variant="primary"
                disabled={
                  !canWrite ||
                  !view.actions.canSelect ||
                  !ideas.some((i) => i.id === activeCandidate)
                }
                aria-busy={busy}
                onClick={() => void useIdea()}
              >
                {busy ? c.selecting : c.use}
              </Button>
              <p>{c.noVideo}</p>
            </div>
          )}
        </>
      )}
      {view.selected && (
        <section className="mj-first-selected">
          <p className="mj-first-selected-mark">✓ {c.selected}</p>
          <div className="mj-first-idea-heading">
            <h1 dir="auto">{chosen?.title}</h1>
            {chosen && (
              <Button
                variant="quiet"
                onClick={() => {
                  setEvidence(chosen.id);
                }}
              >
                {c.inspiration}
              </Button>
            )}
          </div>
          <p dir="auto">{chosen?.angle}</p>
          {script ? (
            <SavedScriptReview
              view={script}
              language={language}
              readOnly={!canWrite}
              onRefresh={refresh}
            />
          ) : data.script ? (
            <p role="status">{c.scriptLoading}</p>
          ) : (
            <div className="mj-first-action">
              <Button
                variant="primary"
                disabled={!canWrite || !view.actions.canPrepareScript}
                onClick={() => void prepare()}
              >
                {c.prepare}
              </Button>
              <p>{c.noVideo}</p>
            </div>
          )}
        </section>
      )}
      {view.history.length > 1 && (
        <details className="mj-first-history">
          <summary>{c.history}</summary>
          <ol>
            {view.history.map((r, index) => (
              <li key={r.revisionId}>
                {r.revisionId === view.revision?.revisionId ? (
                  <span>
                    {index + 1}
                    {r.current ? ` · ${c.current}` : ''}
                  </span>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      next.set('ideaRevision', r.revisionId);
                      setParams(next);
                    }}
                  >
                    {index + 1}
                    {r.current ? ` · ${c.current}` : ''}
                  </Button>
                )}
                {' · '}
                <time dateTime={r.createdAt}>
                  {new Date(r.createdAt).toLocaleString(language)}
                </time>
              </li>
            ))}
          </ol>
        </details>
      )}
      {evidence && (
        <SidePanel
          title={c.inspiration}
          eyebrow={null}
          closeLabel={c.close}
          onClose={() => {
            setEvidence(null);
          }}
          footer={null}
        >
          <div className="mj-first mj-first-modal">
            {direction && direction.payload.contentIdea.references.length > 0 ? (
              <ResearchReferenceList
                language={language}
                copy={ws}
                ideas={[
                  {
                    artifactId: direction.artifactId,
                    title: direction.payload.title,
                    basis: direction.payload.contentIdea.basis,
                    references: direction.payload.contentIdea.references,
                  },
                ]}
              />
            ) : (
              <p>{c.noEvidence}</p>
            )}
          </div>
        </SidePanel>
      )}
      {adjust && (
        <Modal
          title={c.adjust}
          description={c.adjustCost}
          onClose={() => {
            if (!busy) setAdjust(null);
          }}
          onSubmit={() => void update()}
          footer={
            <>
              <Button
                disabled={busy}
                onClick={() => {
                  setAdjust(null);
                }}
              >
                {c.cancel}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={busy || !adjustment.trim()}
              >
                {busy ? c.adjusting : c.update}
              </Button>
            </>
          }
        >
          <div className="mj-first">
            <Field id="first-idea-adjustment" label={c.adjustField} hint={c.adjustHint}>
              <textarea
                id="first-idea-adjustment"
                className="mj-ws-inp"
                dir="auto"
                maxLength={2000}
                rows={4}
                data-autofocus="1"
                value={adjustment}
                disabled={busy}
                onChange={(e) => {
                  setAdjustment(e.target.value);
                }}
              />
            </Field>
            {actionFailed && <p role="alert">{c.changeFailed}</p>}
          </div>
        </Modal>
      )}
    </section>
  );
}
